import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config.js';
import dayjs from 'dayjs';

import '../../styles/global.css';
import '../../styles/requests.css'

import PageHeader from '../general/pageHeader.js';
import Loading from '../general/loading.js';
import { Autocomplete, TextField } from '@mui/material';
import CreateAmenityBookingForm from '../forms/createAmenityBookingForm.js';
import useAuthenticate from '../authentication/authenticate.js';

import AmenityBookingCalendar from './AmenityBookingCalendar.js';
import EmptyAmenityState from './EmptyAmenity.js';

const AmenityBooking = ({ onBack, onBookingComplete }) => {
  const { user } = useAuthenticate();
  const role = Object.keys(user?.user?.active || {})[0];
  const accountId = user?.user?.accounts?.[role];

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedSlots, setSelectedSlots] = useState([]);

  const { data: properties, isLoading } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  const { data: amenities = [] } = useQuery({
    queryKey: ['amenities', selectedProperty?.value],
    queryFn: () => fetchAmenities(selectedProperty?.value),
    enabled: !!selectedProperty,
  });

  const { data: amenitySettings } = useQuery({
    queryKey: ['amenityBookingSettings', selectedAmenity?._id],
    queryFn: () => fetchAmenitySettings(selectedAmenity?._id),
    enabled: !!selectedAmenity,
  });

  const { data: amenitiesData = [] } = useQuery({
    queryKey: ['amenityBookings', selectedAmenity?._id],
    queryFn: () => fetchBookingsByAmenity(selectedAmenity?._id),
    enabled: !!selectedAmenity,
  });

  // Auto-select first property
  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  // Reset when amenity changes
  useEffect(() => {
    if (selectedAmenity) {
      setSelectedDate(dayjs());
      setSelectedSlots([]);
    }
  }, [selectedAmenity]);

  const handleBooking = () => {
    setShowBookingForm(true);
  };

  const handleBookingSuccess = (bookingData) => {
    setSelectedSlots([]);
    setShowBookingForm(false);
    console.log('Booking confirmed:', bookingData);

    if (onBookingComplete) {
            onBookingComplete();
        }
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
  };

  const getStartEndTime = (slots) => {
    if (slots.length === 0) return { startTime: '', endTime: '' };
    const sortedSlots = slots.sort();
    const startTime = sortedSlots[0];
    const slotSizeMinutes = amenitySettings?.slotSizeMinutes || 30;
    
    let endTime;
    if (slots.length === 1) {
      const start = dayjs(`2000-01-01 ${startTime}`, 'YYYY-MM-DD HH:mm');
      endTime = start.add(slotSizeMinutes, 'minute').format('HH:mm');
    } else {
      endTime = sortedSlots[sortedSlots.length - 1];
    }
    
    return { startTime, endTime };
  };

  return (
    <div className="w-full">
      <PageHeader pageName="Amenity Bookings" />
        <div className="mb-4">
              <button
                onClick={onBack}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition"
              >
                ← Back
              </button>
            </div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => {
                setSelectedProperty(newVal);
                setSelectedAmenity(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
              )}
              sx={{ width: '350px' }}
            />

            <Autocomplete
              options={amenities}
              value={selectedAmenity || null}
              onChange={(e, newVal) => setSelectedAmenity(newVal)}
              isOptionEqualToValue={(option, value) => option?._id === value?._id} 
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => (
                <TextField {...params} label="Select Amenity" variant="outlined" fullWidth />
              )}
              disabled={!selectedProperty}
              sx={{ width: '350px' }}
            />
          </div>

          <div className="amenities-container p-6 bg-white rounded-lg shadow-sm">
              {!selectedAmenity ? (
                <EmptyAmenityState />
              ) : (
                <AmenityBookingCalendar
                  selectedAmenity={selectedAmenity}
                  amenitySettings={amenitySettings}
                  amenitiesData={amenitiesData}
                  selectedDate={selectedDate}
                  selectedSlots={selectedSlots}
                  onDateChange={setSelectedDate}
                  onSlotsChange={setSelectedSlots}
                  onBooking={handleBooking}
                />
              )}
            </div>
          </>
        )}

        {showBookingForm && (
          <CreateAmenityBookingForm
            onRequestCreated={handleBookingSuccess}
            onClose={handleCloseBookingForm}
            prefilledData={{
              amenity: selectedAmenity,
              bookingDate: selectedDate ? selectedDate.format("YYYY-MM-DD") : '',
              bookedBy: accountId,
              role: role,
              ...getStartEndTime(selectedSlots),
            }}
          />
        )}
      </div>
    );
  };

  const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
      value: item._id,
      label: item.name,
      amenities: item.amenities
    }));
  };

  const fetchAmenities = async (propertyId) => {
    const response = await axios.get(
      `${config.API_URL}/v2/amenity?propertyId=${propertyId}`,
      {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );
    console.log('Fetched amenities for property:', response.data);
    return response.data || [];
  };

  const fetchAmenitySettings = async (amenityId) => {
    const response = await axios.get(
      `${config.API_URL}/v2/amenity/booking/setting?amenity=${amenityId}`,
      {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );
    const results = response.data.results || response.data || [];
    return Array.isArray(results) ? results[0] : results;
  };

  const fetchBookingsByAmenity = async (amenityId) => {
    const response = await axios.get(
      `${config.API_URL}/v2/amenity/booking?amenity=${amenityId}`,
      {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );

    return response.data.results || [];
  };

export default AmenityBooking;